import { OnChange } from '@/features/form/types'
import { useLicence } from '@/features/licence/hooks/useLicence'
import { FormKeys } from '@/features/pos/components/CustomerProfile/form/constants/keys'
import {
    AddressInput,
    FormContext,
    FormDefaultValues,
    FormInput,
    FormOutput,
    PhoneInput,
} from '@/features/pos/components/CustomerProfile/form/types'
import { getDefaultValues } from '@/features/pos/components/CustomerProfile/form/utils/getDefaultValues'
import { dynamicResolver } from '@/features/pos/components/CustomerProfile/form/utils/resolver'
import { useCallback, useEffect } from 'react'
import {
    SetValueConfig,
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
    useWatch,
} from 'react-hook-form'

export const useForm = (
    defaultValues: FormDefaultValues,
    isDelivery: boolean
): UseFormReturn<FormInput, FormContext, FormOutput> => {
    const { country } = useLicence()

    const methods = useRHFForm<FormInput, FormContext, FormOutput>({
        defaultValues: getDefaultValues(defaultValues, country),
        resolver: dynamicResolver,
        context: { isDelivery },
    })

    const { reset } = methods

    useEffect(() => {
        reset(getDefaultValues(defaultValues, country))
    }, [defaultValues, country, reset])

    return methods
}

export const useFormPhoneField = (): [PhoneInput, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.Phone>({ name: FormKeys.Phone })

    return [value, onChange]
}

export const useFormNameField = (): [string, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.Name>({ name: FormKeys.Name })

    return [value, onChange]
}

export const useFormAddressField = (): [AddressInput, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<FormInput, FormKeys.Address>({ name: FormKeys.Address })

    return [value, onChange]
}

export const useFormAddressFieldValue = () => {
    return useWatch<FormInput, FormKeys.Address>({ name: FormKeys.Address })
}

export const useFormSetAddressField = (): ((
    address: AddressInput,
    options?: SetValueConfig
) => void) => {
    const { setValue } = useFormContext<FormInput, FormContext, FormOutput>()

    return useCallback(
        (address: AddressInput, options?: SetValueConfig) => {
            setValue(FormKeys.Address, address, options)
        },
        [setValue]
    )
}

export const useFormTriggerAddressField = (): (() => void) => {
    const { trigger } = useFormContext<FormInput, FormContext, FormOutput>()

    return useCallback(() => trigger(FormKeys.Address), [trigger])
}

export const useFormClearAddressErrors = (): (() => void) => {
    const { clearErrors } = useFormContext<FormInput, FormContext, FormOutput>()

    return useCallback(() => clearErrors(FormKeys.Address), [clearErrors])
}

export const useFormReset = (): (() => void) => {
    const { country } = useLicence()
    const { reset } = useFormContext<FormInput, FormContext, FormOutput>()

    return useCallback(() => reset(getDefaultValues({}, country)), [country, reset])
}

export const useFormSubmit = (onFormSubmit: (form: FormOutput) => void): (() => void) => {
    const { handleSubmit } = useFormContext<FormInput, FormContext, FormOutput>()

    const handleValid = async (data: FormOutput) => onFormSubmit(data)

    return handleSubmit(handleValid)
}
